import * as st from './st';
import { getSpellElementIcon } from './weapon';
import { getDmgLines, pickLvl } from './spellUtils';
import { applyWeaponMagingPreview } from './weaponMaging';

const TXT_EN = { fr: 'fr', en: 'en', de: 'de', es: 'es', pt: 'pt' };

const DMG_ELS = ['neutral', 'earth', 'fire', 'air', 'water', 'push'];

const STAT_ELS = {
    [st.INTELLIGENCE_ID]: ['fire'],
    [st.CHANCE_ID]: ['water'],
    [st.AGILITY_ID]: ['air'],
    [st.STRENGTH_ID]: ['earth', 'neutral'],
    89: ['fire'],
    90: ['water'],
    91: ['air'],
    88: ['earth'],
    92: ['neutral'],
    84: ['push'],
};

const ELM_BY_ID = {
    0: 'neutral',
    1: 'earth',
    2: 'fire',
    3: 'water',
    4: 'air',
    5: 'push',
};

const ELM_LBL = {
    neutral: { fr: 'Neutre', en: 'Neutral', de: 'Neutral', es: 'Neutral', pt: 'Neutro' },
    fire: { fr: 'Feu', en: 'Fire', de: 'Feuer', es: 'Fuego', pt: 'Fogo' },
    earth: { fr: 'Terre', en: 'Earth', de: 'Erde', es: 'Tierra', pt: 'Terra' },
    air: { fr: 'Air', en: 'Air', de: 'Luft', es: 'Aire', pt: 'Ar' },
    water: { fr: 'Eau', en: 'Water', de: 'Wasser', es: 'Agua', pt: 'Água' },
    push: { fr: 'Poussee', en: 'Push', de: 'Schubs', es: 'Empuje', pt: 'Empurrão' },
};

const mkStatTag = (id) => `stat:${id}`;
const mkDmgTag = (el) => `dmg:${el}`;

function getStatIconEl(statId) {
    if (statId === st.INTELLIGENCE_ID || statId === 89) return 'fire';
    if (statId === st.CHANCE_ID || statId === 90) return 'water';
    if (statId === st.AGILITY_ID || statId === 91) return 'air';
    if (statId === st.STRENGTH_ID || statId === 88) return 'earth';
    if (statId === 92) return 'neutral';
    if (statId === 84) return 'push';
    return null;
}

function getStatDef(id) {
    return st.STATS.find((x) => Number(x.id) === Number(id)) || null;
}

function getStatTagIcon(statId) {
    const iconEl = getStatIconEl(statId);
    if (iconEl) return getSpellElementIcon(iconEl);
    const def = getStatDef(statId);
    return def?.icon || null;
}

function getActiveStatIds(build) {
    if (!build || typeof build.getStats !== 'function') return [];
    const stats = build.getStats();
    const out = [];
    Object.values(stats || {}).forEach((s) => {
        if (!s || s.id === st.LVL_ID) return;
        if (s.id === st.AP_ID || s.id === st.MP_ID) return;
        const effMin = typeof build.constructor?.getEffectiveMinRange === 'function'
            ? build.constructor.getEffectiveMinRange(s, build.lvl)
            : Number(s.minRange || 0);
        if (build.maximizeTargets?.[s.id] || Number(s.desired) > Number(effMin)) {
            out.push(Number(s.id));
        }
    });
    return out.sort((a, b) => {
        const ao = getStatDef(a)?.order ?? 9999;
        const bo = getStatDef(b)?.order ?? 9999;
        return ao - bo;
    });
}

function addSpellEls(outSet, spell, lvl) {
    if (!spell) return;
    const lv = pickLvl(spell.levels, lvl);
    if (!lv) return;
    const dmg = getDmgLines(lv);
    const push = (line) => {
        if (!line || line.kind !== 'damage') return;
        if (line.el && DMG_ELS.includes(line.el)) outSet.add(line.el);
    };
    (dmg.base || []).forEach(push);
    (dmg.conditional || []).forEach((b) => (b.lines || []).forEach(push));
    (dmg.bonusLines || []).forEach((line) => {
        if (line?.kind === 'base_damage_bonus' && line.el && DMG_ELS.includes(line.el)) {
            outSet.add(line.el);
        }
    });
}

function addWeaponEls(outSet, build) {
    // Either weapon criterion tags the build by what is in the slot. With
    // find-the-best-weapon that is empty until a run fills it, and then the
    // tags describe the weapon the solver chose.
    if (!build?.maximizeWeapon && !build?.maximizeBestWeapon) return;
    const weaponItem = build.getWeapon?.();
    const raw = weaponItem?.weapon || weaponItem;
    if (!raw || !Array.isArray(raw.effects)) return;
    const maging = build.getWeaponMaging?.() ?? null;
    const w = maging ? applyWeaponMagingPreview(raw, maging) : raw;
    w.effects.forEach((ef) => {
        const el = ELM_BY_ID[Number(ef?.element_id)];
        if (el && DMG_ELS.includes(el)) outSet.add(el);
        if (Number(ef?.effect_id) === 5) outSet.add('push');
    });
}

export function buildTagIds({ build, spellById = {} }) {
    if (!build) return [];
    const statIds = getActiveStatIds(build);
    const statTags = [];
    const seenStatVisual = new Set();
    statIds.forEach((id) => {
        const iconEl = getStatIconEl(id);
        const key = iconEl ? `el:${iconEl}` : `stat:${id}`;
        if (seenStatVisual.has(key)) return;
        seenStatVisual.add(key);
        statTags.push(mkStatTag(id));
    });

    const covered = new Set();
    statTags.forEach((tagId) => {
        const id = Number(String(tagId).replace('stat:', ''));
        const iconEl = getStatIconEl(id);
        if (iconEl) covered.add(iconEl);
        (STAT_ELS[id] || []).forEach((el) => covered.add(el));
    });

    const dmgEls = new Set();
    const sids = [
        ...(Array.isArray(build.maximizeSpellIds) ? build.maximizeSpellIds : []),
        ...(Array.isArray(build.maximizeSpellCriteria) ? build.maximizeSpellCriteria : [])
            .map((c) => c?.spell_id ?? c?.spellId),
    ];
    sids.forEach((sid) => addSpellEls(dmgEls, spellById[String(sid)] || spellById[sid], build.lvl));
    addWeaponEls(dmgEls, build);

    const dmgTags = DMG_ELS
        .filter((el) => dmgEls.has(el) && !covered.has(el))
        .map((el) => mkDmgTag(el));

    const all = [...statTags, ...dmgTags];
    const byEl = new Map();
    const other = [];
    all.forEach((tagId) => {
        const s = String(tagId);
        if (s.startsWith('dmg:')) {
            const el = s.slice(4);
            if (DMG_ELS.includes(el) && !byEl.has(el)) byEl.set(el, tagId);
            else other.push(tagId);
            return;
        }
        if (s.startsWith('stat:')) {
            const id = Number(s.slice(5));
            const el = getStatIconEl(id);
            if (el && DMG_ELS.includes(el) && !byEl.has(el)) byEl.set(el, tagId);
            else other.push(tagId);
            return;
        }
        other.push(tagId);
    });
    const elFirst = DMG_ELS.map((el) => byEl.get(el)).filter(Boolean);
    return [...elFirst, ...other];
}

export function normTagIds(raw) {
    if (!Array.isArray(raw)) return [];
    const out = [];
    const seen = new Set();
    raw.forEach((x) => {
        const v = String(x || '').trim();
        if (!v || seen.has(v)) return;
        seen.add(v);
        out.push(v);
    });
    return out;
}

export function sameTagIds(a, b) {
    const aa = normTagIds(a);
    const bb = normTagIds(b);
    if (aa.length !== bb.length) return false;
    for (let i = 0; i < aa.length; i += 1) {
        if (aa[i] !== bb[i]) return false;
    }
    return true;
}

export function tagMeta(tagId, lang = 'en') {
    const l = TXT_EN[lang] || TXT_EN.en;
    const s = String(tagId || '');
    if (s.startsWith('stat:')) {
        const id = Number(s.slice(5));
        const def = getStatDef(id);
        if (!def) return null;
        return {
            id: s,
            icon: getStatTagIcon(id),
            label: def.label?.[l] || def.label?.en || def.label?.fr || String(id),
        };
    }
    if (s.startsWith('dmg:')) {
        const el = s.slice(4);
        if (!DMG_ELS.includes(el)) return null;
        return {
            id: s,
            icon: getSpellElementIcon(el),
            label: ELM_LBL[el]?.[l] || ELM_LBL[el]?.en || el,
        };
    }
    return null;
}
